import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  styled,
  Box,
  Tooltip,
  Card,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  EmojiEmotions,
  Send,
  Close,
  EditNote,
  Attachment,
  KeyboardArrowDown,
  KeyboardVoice,
  Call,
  CalendarMonth,
  VideoCall,
} from '@mui/icons-material';
import { useAddChatMutation, useEditChatMutation } from '../chatApiSlice';
import toast from 'react-hot-toast';
import EmojiPicker from 'emoji-picker-react';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';
import { quillFix } from '../../../utils/quillFix';
import AttachAvatar from '../../../common/AttachAvatar';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUploadProgress, selectUploadStarted, setAttachmentUploadProgress } from '../chatSlice';

const StyledPaper = styled(Paper)({
  padding: '1rem 0.5rem 0.5rem 0.5rem',
  display: 'flex',
  alignItems: 'flex-end',
  background: 'transparent',
  flexDirection: 'column',
  minHeight: '60px',
  position: 'absolute',
  boxShadow: 'none',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 100,
});

const QuillWrapper = styled(Box)(({ theme }) => ({
  border: '1px solid lightgrey',
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  alignContent: 'center',
  width: '100%',
  position: 'relative',
  paddingLeft: '30px',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '15px !important',
  },
  background: 'white',
}));

const StyledQuill = styled(ReactQuill)({
  '& .ql-container': {
    border: 'none',
    border: 0,
    backgroundColor: '#ffffff',
    '&.ql-focused': {
      boxShadow: '#3f51b5 0 0 0 2px',
    },
  },
  '& .ql-container.ql-snow': {
    height: 'auto',
    border: 0,
  },
  '& .ql-editor': {
    maxHeight: '150px',
    minHeight: '40px',
    overflowY: 'scroll',
    border: 0,
  },
  '& .ql-toolbar': {
    display: 'none',
  },
  '.ql-editor.ql-blank::before': {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  '&.show-toolbar .ql-toolbar': {
    display: 'block',
    border: 'none',
    background: 'white',
  },
  '.ql-tooltip': {
    bottom: 'auto !important',
    top: '-47px !important' /* Adjust the value as needed */,
    left: '0% !important',
    boxShadow: 'none',
    borderRadius: '8px',
    marginTop: '0% !important',
  },
  '.ql-tooltip > input': {
    padding: '5px 10px',
    maxWidth: 120,
    borderRadius: '8px',
    outlineColor: 'lightgray',
    outlineWidth: '0.2px',
    cursor: 'default',
    '&:hover': {
      backgroundColor: '#f1f1f1',
    },
  },
});

const validationSchema = Yup.object({
  chat: Yup.string()
    .trim()
    .matches(/.*\S.*/, 'Chat must contain at least one non-whitespace character.')
    .required('Chat is required'),
  attachments: Yup.array().of(Yup.mixed()).max(4, 'You can upload up to 4 attachments.').notRequired(),
});

const ChatForm = ({
  editingChat,
  setEditingChat,
  showScrollButton,
  onChange,
  inputRef,
  showToolbar,
  setShowToolbar,
  disabled,
  setDisabled,
}) => {
  const [addChat] = useAddChatMutation();
  const [editChat] = useEditChatMutation();
  const [filePreviews, setFilePreviews] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const quillRef = useRef(null);
  const open = Boolean(anchorEl);
  const { empCode } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Detect if the screen is mobile
  const uploadProgress = useSelector(selectUploadProgress);
  const uploadStarted = useSelector(selectUploadStarted);
  const dispatch = useDispatch();
  useEffect(() => {
    //sending the progress status to webview
    window['ReactNativeWebView'] &&
      window['ReactNativeWebView']?.postMessage(
        JSON.stringify({ type: 'upload', progress: uploadProgress, uploadStarted: uploadStarted })
      );
  }, [uploadProgress, uploadStarted]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      chat: '',
      attachments: [],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      quillRef.current.getEditor().focus(); // Focus the Quill editor after submission

      const { chat, attachments } = values;
      const slicedText = quillFix(chat);
      if (slicedText) {
        const formData = new FormData();
        formData.append('message', slicedText);
        formData.append('receiverCode', empCode);
        if (attachments) {
          attachments.forEach((file) => {
            formData.append('attachments', file);
          });
        }
        const action = editingChat
          ? editChat({
              id: editingChat.chatId,
              data: { message: slicedText },
            })
          : addChat(formData);

        try {
          // await toast.promise(action.unwrap(), {
          //   loading: 'Processing chat...',
          //   success: editingChat ? 'Chat edited successfully!' : 'Chat submitted successfully!',
          //   error: err => err.data?.message || 'Error processing chat. Please try again.',
          // });
          await action.unwrap();
          setEditingChat(null);
          resetForm();
          setFilePreviews([]);
          quillRef.current.getEditor().focus(); // Focus the Quill editor after submission
        } catch (error) {
          console.error('Error processing chat:', error);
          resetForm();
        }
      }
    },
  });

  useEffect(() => {
    if (editingChat) {
      formik.setValues({
        chat: editingChat.message,
      });
    }
  }, [editingChat]);

  const handleEmojiPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleEmojiClick = (emojiObject) => {
    const quillText = formik.values.chat;
    const trimmedChat =
      quillText.startsWith('<p><br></p>') || !quillText.includes('</p><p><br></p>')
        ? quillText.replaceAll('<p><br></p>', '').trim()
        : quillText.replaceAll('<p><br></p>', '<br>').trim();

    const formattedText = trimmedChat.endsWith('</p>')
      ? trimmedChat.slice(0, trimmedChat.length - 4) + emojiObject.emoji + '</p>'
      : trimmedChat + emojiObject.emoji;

    formik.setFieldValue('chat', formattedText);
    setAnchorEl(null);
  };

  const handleQuillChange = (content) => {
    formik.setFieldValue('chat', content);
    onChange(inputRef.current.clientHeight);
  };

  const handleCancelEditing = () => {
    setEditingChat(null);
    formik.resetForm();
    setFilePreviews([]);
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files);
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'video/mp4'];
    const validFiles = files.filter((file) => allowedTypes.includes(file.type));
    const newAttachments = [...formik.values.attachments, ...validFiles].slice(0, 4);
    formik.setFieldValue('attachments', newAttachments);

    const previews = newAttachments.map((file) => {
      const fileType = file.type.split('/')[0];
      if (fileType === 'video') {
        return {
          type: 'video',
          url: URL.createObjectURL(file),
        };
      } else {
        return {
          type: 'image',
          url: URL.createObjectURL(file),
        };
      }
    });
    setFilePreviews(previews);
  };

  const handleRemoveAttachment = (index) => {
    const newAttachments = formik.values.attachments.filter((_, i) => i !== index);
    formik.setFieldValue('attachments', newAttachments);
    const newPreviews = filePreviews.filter((_, i) => i !== index);
    setFilePreviews(newPreviews);
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        formik.handleSubmit();
      }
    },
    [formik]
  );

  // useEffect(() => {
  //   const quillEditor = quillRef.current.getEditor();
  //   const quillEditorInput = quillEditor.root;

  //   quillEditorInput.addEventListener('keydown', handleKeyDown);

  //   return () => {
  //     quillEditorInput.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, [handleKeyDown]);

  return (
    <StyledPaper ref={inputRef}>
      <form
        onSubmit={formik.handleSubmit}
        style={{ width: '100%', display: 'flex', alignItems: 'flex-end', position: 'relative', gap: 10 }}
      >
        <QuillWrapper>
          <StyledQuill
            // onKeyDown={handleKeyDown}
            ref={quillRef}
            theme='snow'
            placeholder='Type a message...'
            value={formik.values.chat}
            onChange={(content) => handleQuillChange(content)}
            modules={{
              toolbar: [
                ['bold', 'italic', 'strike', 'blockquote'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link'],
              ],
            }}
            formats={['bold', 'italic', 'strike', 'blockquote', 'list', 'bullet', 'link']}
            className={showToolbar ? 'show-toolbar' : ''}
            sx={{
              width: 'calc(100% - 80px)',
              marginBottom: 0,
              border: 'none',
              '& .ql-container': {
                border: 'none',
              },
            }}
            readOnly={disabled}
          />
          {!isMobile && (
            <InputAdornment
              position='end'
              sx={{ position: 'absolute', left: '-7px', bottom: '22px', display: 'flex', gap: '10px' }}
            >
              <IconButton onClick={handleEmojiPopover} edge='end' disabled={disabled}>
                <EmojiEmotions />
              </IconButton>
            </InputAdornment>
          )}
          <InputAdornment
            position='end'
            sx={{ position: 'absolute', right: '15px', bottom: '22px', display: 'flex', gap: '10px' }}
          >
            {/* <KeyboardVoice sx={{ cursor: 'default', color: 'grey' }} /> */}

            {true ? null : (
              <>
                <input type='file' multiple hidden id='attachment-input' onChange={handleAttachmentChange} />
                <label htmlFor='attachment-input'>
                  <Tooltip title='Attachments'>
                    <IconButton component='span' sx={{ cursor: 'default', marginRight: '-5px' }} disabled={disabled}>
                      <Attachment />
                    </IconButton>
                  </Tooltip>
                </label>
              </>
            )}
            <IconButton onClick={() => setShowToolbar(!showToolbar)} edge='end' disabled={disabled}>
              {showToolbar ? <KeyboardArrowDown /> : <EditNote />}
            </IconButton>
            <IconButton type='submit' edge='end' disabled={disabled}>
              <Send />
            </IconButton>
          </InputAdornment>
          {editingChat ? (
            <InputAdornment
              position='end'
              sx={{
                position: 'absolute',
                left: '-3px',
                top: '20px',
                display: 'flex',
                gap: '10px',
              }}
            >
              <IconButton
                onClick={handleCancelEditing}
                edge='end'
                sx={{ background: 'lightgrey', borderRadius: '50%' }}
                disabled={disabled}
              >
                <Close sx={{ fontSize: '12px', m: '-5px', color: 'black' }} />
              </IconButton>
            </InputAdornment>
          ) : null}
        </QuillWrapper>
        <Card
          sx={{
            background: formik.values?.attachments?.length > 0 ? 'white' : 'none',
            boxShadow: formik.values?.attachments?.length > 0 ? null : 'none',
            borderRadius: '20px',
            display: 'flex',
            justifyContent: 'center',
            padding: '10px',
            gap: 3,
            zIndex: 101,
            position: 'absolute',
            top: '-71px',
            left: 0,
          }}
        >
          <Box sx={{}}>
            {formik.values.attachments && formik.values.attachments.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-start',
                  gap: 1,
                  flexWrap: 'wrap',
                  cursor: 'default',
                }}
              >
                {filePreviews.map((preview, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mr: '10px', position: 'relative' }}>
                    <Tooltip title={formik.values.attachments[index].name} arrow>
                      <AttachAvatar
                        src={preview.url}
                        isVideoPreview={preview.type === 'video'}
                        publicCall={false}
                        variant='square'
                        sx={{ width: 50, height: 50 }}
                      />
                    </Tooltip>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        right: '-15px',
                        top: '0%',
                        transform: 'translateY(-50%)',
                        background: 'lightgrey',
                        borderRadius: '50%',
                        cursor: 'default',
                      }}
                      disabled={disabled}
                      onClick={() => handleRemoveAttachment(index)}
                    >
                      <Close sx={{ fontSize: '12px', m: '-5px', color: 'black' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Card>
        <Popover
          id='emoji-picker-popover'
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <EmojiPicker onEmojiClick={handleEmojiClick} />
        </Popover>
      </form>
    </StyledPaper>
  );
};

export default ChatForm;
